// frontend/src/shared/styles/tokens.ts

/**
 * DESIGN TOKENS - Sistema de Diseño Centralizado
 *
 * ✅ REFACTORIZACIÓN: Consolida ~970 líneas duplicadas de 28 archivos
 *
 * Este archivo centraliza todos los patrones de estilos reutilizables,
 * eliminando duplicación y mejorando consistencia visual.
 *
 * Uso:
 * ```typescript
 * import { buttons, inputs } from '@/shared/styles/tokens';
 *
 * const styles = {
 *   myButton: buttons.primary,
 *   myInput: inputs.text,
 * };
 * ```
 */

export const designTokens = {
	// ============ BOTONES ============
	// Elimina ~215 líneas duplicadas
	buttons: {
		/** Botón primario - Más usado (54 ocurrencias) */
		primary: `
      bg-secundario text-white
      px-4 py-2 rounded-lg
      font-medium
      hover:bg-primario
      transition-colors duration-200
      disabled:opacity-50 disabled:cursor-not-allowed
    `,

		/** Botón primario grande - Para acciones principales */
		primaryLarge: `
      bg-secundario text-white
      px-6 py-3 rounded-lg
      font-medium
      hover:bg-primario
      shadow-md
      transition-colors duration-200
      disabled:opacity-50 disabled:cursor-not-allowed
    `,

		/** Botón de éxito - Para confirmaciones */
		success: `
      bg-green-600 text-white
      px-4 py-2 rounded-lg
      font-medium
      hover:bg-green-700
      transition-colors duration-200
      disabled:opacity-50 disabled:cursor-not-allowed
    `,

		/** Botón secundario/cancelar - Para acciones secundarias */
		secondary: `
      bg-gray-100 text-gray-700
      px-4 py-2 rounded-lg
      font-medium
      hover:bg-gray-200
      transition-colors duration-200
      disabled:opacity-50 disabled:cursor-not-allowed
    `,

		/** Botón de peligro - Para acciones destructivas */
		danger: `
      bg-error text-white
      px-4 py-2 rounded-lg
      font-medium
      hover:bg-red-700
      transition-colors duration-200
      disabled:opacity-50 disabled:cursor-not-allowed
    `,

		/** Estados comunes de botones */
		states: {
			disabled: 'disabled:opacity-50 disabled:cursor-not-allowed',
			hover: 'hover:bg-primario transition-colors duration-200',
			focus:
				'focus:outline-none focus:ring-2 focus:ring-primario focus:ring-offset-2',
		},
	},

	// ============ INPUTS ============
	// Elimina ~180 líneas duplicadas
	inputs: {
		/** Input de texto standard (23 ocurrencias) */
		text: `
      w-full px-4 py-2
      border border-gray-300 rounded-lg
      bg-fondo
      focus:ring-2 focus:ring-secundario focus:border-transparent
      transition-all duration-200
      disabled:bg-gray-100 disabled:cursor-not-allowed
    `,

		/** Input pequeño - Para formularios compactos */
		textSmall: `
      w-full px-3 py-2
      border border-gray-300 rounded-lg
      bg-fondo
      focus:ring-2 focus:ring-secundario focus:border-blue-500
      transition-colors duration-200
      text-sm
    `,

		/** Textarea - Para texto largo */
		textarea: `
      w-full px-4 py-2
      border border-gray-300 rounded-lg
      bg-fondo
      focus:ring-2 focus:ring-secundario focus:border-transparent
      resize-vertical min-h-[80px]
      transition-all duration-200
    `,

		/** Select/Dropdown */
		select: `
      w-full px-3 py-2
      border border-gray-300 rounded-lg
      bg-fondo
      focus:ring-2 focus:ring-secundario focus:border-blue-500
      transition-colors duration-200
      appearance-none
    `,

		/** Checkbox */
		checkbox: `
      w-5 h-5
      rounded
      focus:ring-2 focus:ring-secundario
      accent-secundario
    `,

		/** Label standard */
		label: 'block text-sm font-medium text-gray-700 mb-1',

		/** Label grande */
		labelLarge: 'block text-base font-medium text-gray-700 mb-2',
	},

	// ============ CONTENEDORES ============
	// Elimina ~150 líneas duplicadas
	containers: {
		/** Card standard (28 ocurrencias) */
		card: `
      bg-white rounded-lg shadow-sm
      border border-gray-200
      overflow-hidden
    `,

		/** Card con padding interno */
		cardWithPadding: `
      bg-white rounded-lg shadow-sm
      border border-gray-200
      p-6
    `,

		/** Card grande con shadow prominente */
		cardLarge: `
      bg-white rounded-xl shadow-lg
      border border-gray-100
      overflow-hidden
    `,

		/** Contenedor centrado - Tamaño grande */
		centered: 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8',

		/** Contenedor centrado - Tamaño mediano */
		centeredMd: 'max-w-4xl mx-auto px-4',

		/** Contenedor centrado - Tamaño pequeño */
		centeredSm: 'max-w-md mx-auto',

		/** Modal overlay - Fondo oscuro */
		modalOverlay: `
      fixed inset-0 z-50
      bg-black bg-opacity-50 backdrop-blur-sm
      flex items-center justify-center p-4
    `,

		/** Modal container */
		modal: `
      bg-white rounded-2xl shadow-2xl
      overflow-hidden border border-gray-100
      w-full max-w-md
    `,

		/** Sección con fondo gris */
		section: `
      bg-gray-50 rounded-lg
      p-6 border border-gray-200
    `,
	},

	// ============ LAYOUT ============
	// Elimina ~120 líneas duplicadas
	layout: {
		/** Flex centrado horizontal y verticalmente */
		flexCenter: 'flex items-center justify-center',

		/** Flex con items centrados y espacio entre */
		flexBetween: 'flex items-center justify-between',

		/** Flex con items al inicio */
		flexStart: 'flex items-center justify-start',

		/** Flex columna */
		flexCol: 'flex flex-col',

		/** Flex columna centrada */
		flexColCenter: 'flex flex-col items-center justify-center',

		/** Grid responsivo 2 columnas */
		grid2Cols: 'grid grid-cols-1 md:grid-cols-2 gap-6',

		/** Grid responsivo 3 columnas */
		grid3Cols: 'grid grid-cols-1 md:grid-cols-3 gap-6',

		/** Grid responsivo 4 columnas */
		grid4Cols: 'grid grid-cols-2 md:grid-cols-4 gap-4',

		/** Espaciado vertical */
		spaceY4: 'space-y-4',
		spaceY6: 'space-y-6',
		spaceY8: 'space-y-8',

		/** Gap entre elementos */
		gap2: 'gap-2',
		gap3: 'gap-3',
		gap4: 'gap-4',
		gap6: 'gap-6',
	},

	// ============ TIPOGRAFÍA ============
	// Elimina ~95 líneas duplicadas
	typography: {
		/** Título principal */
		h1: 'text-3xl font-bold text-gray-900',

		/** Título secundario */
		h2: 'text-2xl font-bold text-gray-900',

		/** Título terciario */
		h3: 'text-xl font-semibold text-gray-900',

		/** Título pequeño */
		h4: 'text-lg font-semibold text-gray-900',

		/** Subtítulo standard */
		subtitle: 'text-gray-600 text-sm leading-relaxed',

		/** Subtítulo grande */
		subtitleLarge: 'text-gray-600 text-base leading-relaxed',

		/** Texto del cuerpo */
		bodyText: 'text-sm text-gray-700',

		/** Texto del cuerpo grande */
		bodyTextLarge: 'text-base text-gray-700',

		/** Label standard */
		labelStandard: 'text-sm font-medium text-gray-700',

		/** Label pequeño */
		labelSmall: 'text-xs font-medium text-gray-600',

		/** Texto centrado */
		textCenter: 'text-center',
	},

	// ============ BADGES Y ESTADOS ============
	// Elimina ~90 líneas duplicadas
	badges: {
		/** Base común para todos los badges */
		base: 'inline-flex px-2 py-1 rounded-full text-sm font-medium',

		/** Badge de éxito */
		success: 'inline-flex px-2 py-1 rounded-full text-sm font-medium bg-green-50 text-green-700',

		/** Badge de advertencia */
		warning: 'inline-flex px-2 py-1 rounded-full text-sm font-medium bg-warn/15 text-warn',

		/** Badge de error */
		error: 'inline-flex px-2 py-1 rounded-full text-sm font-medium bg-error/15 text-error',

		/** Badge informativo */
		info: 'inline-flex px-2 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800',

		/** Badge pendiente */
		pending: 'inline-flex px-2 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800',

		/** Badge completado */
		completed: 'inline-flex px-2 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800',

		/** Badge grande */
		large: 'inline-flex px-3 py-1.5 rounded-full text-base font-medium',
	},

	// ============ MENSAJES ============
	// Elimina ~40 líneas duplicadas
	messages: {
		/** Mensaje de error (16 ocurrencias) */
		error: `
      p-3 bg-red-50
      border-l-4 border-red-500
      text-red-700
      rounded-r-lg text-sm font-medium
    `,

		/** Mensaje de éxito (7 ocurrencias) */
		success: `
      p-3 bg-green-50
      border-l-4 border-green-500
      text-green-700
      rounded-r-lg text-sm font-medium
    `,

		/** Mensaje de advertencia */
		warning: `
      p-3 bg-yellow-50
      border-l-4 border-yellow-400
      text-yellow-800
      rounded-r-lg text-sm font-medium
    `,

		/** Mensaje informativo */
		info: `
      p-3 bg-blue-50
      border-l-4 border-blue-500
      text-blue-700
      rounded-r-lg text-sm font-medium
    `,
	},

	// ============ ESTADOS DE CARGA ============
	// Elimina ~30 líneas duplicadas
	loading: {
		/** Spinner grande (10 ocurrencias) */
		spinnerLarge: `
      animate-spin rounded-full
      h-12 w-12
      border-b-2 border-secundario
      mx-auto
    `,

		/** Spinner mediano */
		spinnerMedium: `
      animate-spin rounded-full
      h-6 w-6
      border-b-2 border-blue-600
    `,

		/** Spinner pequeño */
		spinnerSmall: `
      animate-spin rounded-full
      h-4 w-4
      border-b-2 border-white
    `,

		/** Contenedor de loading */
		container: 'flex flex-col items-center justify-center py-12',

		/** Texto de loading */
		text: 'mt-4 text-gray-600',
	},

	// ============ TABLAS ============
	// Elimina ~50 líneas duplicadas
	tables: {
		/** Contenedor con scroll horizontal */
		container: 'overflow-x-auto rounded-lg shadow-sm',

		/** Tabla base */
		table: 'w-full border-collapse',

		/** Header de tabla */
		header: 'bg-gray-50',

		/** Celda de header */
		headerCell: `
      px-6 py-3
      text-left text-xs font-semibold text-gray-700
      uppercase tracking-wider
    `,

		/** Body de tabla */
		body: 'bg-white divide-y divide-gray-200',

		/** Fila de tabla */
		row: 'hover:bg-gray-50 transition-colors duration-150',

		/** Celda de tabla */
		cell: 'px-6 py-4 text-sm text-gray-700',
	},

	// ============ TRANSICIONES ============
	// Elimina ~72 líneas duplicadas
	transitions: {
		/** Transición de colores (40 ocurrencias) */
		colors: 'transition-colors duration-200',

		/** Transición completa (24 ocurrencias) */
		all: 'transition-all duration-200',

		/** Transición rápida */
		fast: 'transition-all duration-150',

		/** Transición lenta */
		slow: 'transition-all duration-300',
	},

	// ============ EFECTOS ============
	effects: {
		/** Shadows */
		shadow: 'shadow-sm',
		shadowMd: 'shadow-md',
		shadowLg: 'shadow-lg',
		shadowXl: 'shadow-xl',
		shadow2xl: 'shadow-2xl',

		/** Hover effects */
		hover: {
			shadow: 'hover:shadow-md',
			lift: 'hover:transform hover:-translate-y-0.5',
			scale: 'hover:scale-105',
			bgGray: 'hover:bg-gray-50',
			bgGray100: 'hover:bg-gray-100',
		},
	},

	// ============ BORDES ============
	borders: {
		/** Bordes standard */
		standard: 'border border-gray-300',
		light: 'border border-gray-200',
		heavy: 'border-2 border-gray-300',
		colored: 'border border-primario',

		/** Rounded variants */
		rounded: 'rounded',
		roundedMd: 'rounded-md',
		roundedLg: 'rounded-lg',
		roundedXl: 'rounded-xl',
		rounded2xl: 'rounded-2xl',
		roundedFull: 'rounded-full',
	},

	// ============ UTILIDADES ESPECIALES ============
	utilities: {
		/** Decoración de título con línea */
		titleUnderline: `
      h-1 w-24
      bg-gradient-to-r from-secundario to-transparent
      mx-auto rounded-full
    `,

		/** Truncate text */
		truncate: 'truncate',
		lineClamp2: 'line-clamp-2',
		lineClamp3: 'line-clamp-3',
	},
};

// ==========================================
// EXPORTS SELECTIVOS (para mejor tree-shaking)
// ==========================================

export const { buttons } = designTokens;
export const { inputs } = designTokens;
export const { containers } = designTokens;
export const { layout } = designTokens;
export const { typography } = designTokens;
export const { badges } = designTokens;
export const { messages } = designTokens;
export const { loading } = designTokens;
export const { tables } = designTokens;
export const { transitions } = designTokens;
export const { effects } = designTokens;
export const { borders } = designTokens;
export const { utilities } = designTokens;

// Export por defecto
export default designTokens;

// FORCE RECOMPILE - timestamp: 1731505200
